<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('Quarterly Report: July to September') }} {{ thisYear }}
        </oxd-text>
      </div>

      <div class="orangehrm-header-container">
        <oxd-button
          :label="$t('Download Form 941')"
          display-type="secondary"
          @click="onClickDownload"
        />
        <oxd-button
          :label="$t('Download SUTA CSV')"
          display-type="secondary"
          @click="onClickDownloadSuta"
        />
        <oxd-button
          :label="efileRunning ? $t('...') : $t('eFile Form 941 Q3 to IRS')"
          display-type="secondary"
          :disabled="efile || f941transmitted"
          @click="onClickeFile"
        />
      </div>

      <div class="orangehrm-header-container taxbandits-stage-row">
        <oxd-button
          :label="createStage.label"
          :display-type="createStage.displayType"
          :class="createStage.className"
          :disabled="f941created"
        />
        <oxd-button
          :label="validateStage.label"
          :display-type="validateStage.displayType"
          :class="validateStage.className"
          :disabled="f941validated"
        />
        <oxd-button
          :label="transmitStage.label"
          :display-type="transmitStage.displayType"
          :class="transmitStage.className"
          :disabled="f941transmitted"
        />
      </div>

      <div class="taxbandits-progress-wrap">
        <div class="taxbandits-progress-step" :class="getStepClass('create')">
          <div class="taxbandits-progress-dot">1</div>
          <div class="taxbandits-progress-text">
            <div class="taxbandits-progress-title">Create</div>
            <div class="taxbandits-progress-subtitle">
              {{ getStepSubtitle('create') }}
            </div>
          </div>
        </div>

        <div class="taxbandits-progress-line"></div>

        <div class="taxbandits-progress-step" :class="getStepClass('validate')">
          <div class="taxbandits-progress-dot">2</div>
          <div class="taxbandits-progress-text">
            <div class="taxbandits-progress-title">Validate</div>
            <div class="taxbandits-progress-subtitle">
              {{ getStepSubtitle('validate') }}
            </div>
          </div>
        </div>

        <div class="taxbandits-progress-line"></div>

        <div class="taxbandits-progress-step" :class="getStepClass('transmit')">
          <div class="taxbandits-progress-dot">3</div>
          <div class="taxbandits-progress-text">
            <div class="taxbandits-progress-title">Transmit</div>
            <div class="taxbandits-progress-subtitle">
              {{ getStepSubtitle('transmit') }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="efileMessage" class="taxbandits-message-box">
        <oxd-text tag="p">{{ efileMessage }}</oxd-text>
      </div>

      <div class="orangehrm-container">
        <table-header :total="mtotal" :loading="misLoading"></table-header>
        <div class="orangehrm-container">
          <oxd-card-table
            :loading="misLoading"
            :clickable="true"
            :headers="mheaders"
            :items="quarterlyItems"
            row-decorator="oxd-table-decorator-card"
          />
        </div>
      </div>

      <table-header :total="total" :loading="isLoading"></table-header>

      <div class="orangehrm-container">
        <oxd-card-table
          :loading="isLoading"
          :clickable="true"
          :headers="headers"
          :items="items?.data"
          row-decorator="oxd-table-decorator-card"
          @click="onClickEdit"
        />
      </div>

      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>

      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
        <oxd-text tag="h4" class="orangehrm-main-title">
          Total Gross: ${{ totalCostSum.toFixed(2) }} Taxable SUTA: ${{
            taxablesuta.toFixed(2)
          }}
          SUTA: ${{ suta.toFixed(2) }}
        </oxd-text>
      </div>
    </div>

    <confirmation-dialog
      ref="confirmDialog"
      :title="$t('general.confirmation_required')"
      :subtitle="$t('This will submit form 941 Q3 to IRS')"
      :cancel-label="$t('general.no_cancel')"
      :confirm-label="$t('leave.yes_confirm')"
      confirm-button-type="secondary"
    ></confirmation-dialog>
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import ConfirmationDialog from '@/core/components/dialogs/ConfirmationDialog';
import {computed} from 'vue';

function aggregateGrossPay(data) {
  const aggregation = data.reduce((acc, {empNumber, grossPay}) => {
    if (acc[empNumber]) {
      acc[empNumber] += parseFloat(grossPay);
    } else {
      acc[empNumber] = parseFloat(grossPay);
    }
    return acc;
  }, {});

  return aggregation;
}

export default {
  name: 'PayReportsQ3',

  components: {
    'confirmation-dialog': ConfirmationDialog,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/payreportsq3',
    );
    const http1 = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/deductions',
    );

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http);
    // Mask SSN helper
    const maskSSN = (ssn) => {
      if (!ssn) return 'xxx-xx-xxxx';

      const digits = ssn.replace(/\D/g, '');
      const last4 = digits.slice(-4);

      return `xxx-xx-${last4 || 'xxxx'}`;
    };

    // Computed list with masked SSN
    const maskedItems = computed(() => {
      const res = response.value || {};

      const data = Array.isArray(res.data)
        ? res.data.map((item) => ({
            ...item,
            ssn: maskSSN(item.ssn),
          }))
        : [];
      return {
        ...res,
        data,
      };
    });
    return {
      http,
      http1,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: maskedItems,
    };
  },

  data() {
    return {
      efileRunning: false,
      efileMessage: '',
      efileStep: '',
      thisYear: null,
      checkedItems: [],
      misLoading: false,
      mtotal: 3,
      mheaders: [
        {name: 'month', title: 'Month', style: {'flex-basis': '80%'}},
        {name: 'federal', title: 'Federal', style: {'flex-basis': '80%'}},
        {
          name: 'socialSecurity',
          title: 'Social Security',
          style: {'flex-basis': '80%'},
        },
        {name: 'medicare', title: 'Medicare', style: {'flex-basis': '80%'}},
        {name: 'total', title: 'Total', style: {'flex-basis': '80%'}},
      ],
      headers: [
        {
          name: 'ssn',
          slot: 'title',
          title: this.$t('SSN'),
          style: {'flex-basis': '80%'},
        },
        {
          name: 'firstName',
          slot: 'title',
          title: this.$t('general.first_name'),
          style: {'flex-basis': '80%'},
        },
        {
          name: 'lastName',
          slot: 'title',
          title: this.$t('general.last_name'),
          style: {'flex-basis': '80%'},
        },
        {
          name: 'quarterGross',
          slot: 'title',
          title: this.$t('pim.gross_pay'),
          style: {'flex-basis': '80%'},
        },
      ],
    };
  },

  created() {
    this.getTaxYear();
  },

  computed: {
    meta() {
      return this.items?.meta || {};
    },

    totalCostSum() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.qtotal || 0;
    },

    taxablesuta() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.taxablesuta || 0;
    },

    suta() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.suta || 0;
    },

    efile() {
      if (!Array.isArray(this.items?.data)) return false;
      return this.meta.efile || false;
    },

    julss() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.julss || 0;
    },

    augss() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.augss || 0;
    },

    sepss() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.sepss || 0;
    },

    julmed() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.julmed || 0;
    },

    augmed() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.augmed || 0;
    },

    sepmed() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.sepmed || 0;
    },

    julfed() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.julfed || 0;
    },

    augfed() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.augfed || 0;
    },

    sepfed() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.sepfed || 0;
    },

    jultax() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.jultax || 0;
    },

    augtax() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.augtax || 0;
    },

    septax() {
      if (!Array.isArray(this.items?.data)) return 0;
      return this.meta.septax || 0;
    },

    f941created() {
      return this.meta.created941 === 'Yes';
    },

    f941validated() {
      return this.meta.validated941 === 'Yes';
    },

    f941transmitted() {
      return this.meta.transmitted941 === 'Yes';
    },

    createStage() {
      if (this.efileRunning && this.efileStep === 'create') {
        return {
          label: 'Creating...',
          displayType: 'secondary',
          className: '',
        };
      }

      if (this.f941created) {
        return {
          label: 'F941 Created',
          displayType: 'secondary',
          className: 'taxbandits-success-btn',
        };
      }

      return {
        label: 'F941 not created',
        displayType: 'danger',
        className: '',
      };
    },

    validateStage() {
      if (this.efileRunning && this.efileStep === 'validate') {
        return {
          label: 'Validating...',
          displayType: 'secondary',
          className: '',
        };
      }

      if (this.f941validated) {
        return {
          label: 'F941 Validated',
          displayType: 'secondary',
          className: 'taxbandits-success-btn',
        };
      }

      return {
        label: 'F941 not Validated',
        displayType: 'danger',
        className: '',
      };
    },

    transmitStage() {
      if (this.efileRunning && this.efileStep === 'transmit') {
        return {
          label: 'Transmitting...',
          displayType: 'secondary',
          className: '',
        };
      }

      if (this.f941transmitted) {
        return {
          label: 'F941 Transmitted',
          displayType: 'secondary',
          className: 'taxbandits-success-btn',
        };
      }

      return {
        label: 'F941 not Transmitted',
        displayType: 'danger',
        className: '',
      };
    },

    quarterlyItems() {
      return [
        {
          id: 1,
          month: 'July',
          federal: this.julfed,
          socialSecurity: this.julss,
          medicare: this.julmed,
          total: this.jultax,
        },
        {
          id: 2,
          month: 'August',
          federal: this.augfed,
          socialSecurity: this.augss,
          medicare: this.augmed,
          total: this.augtax,
        },
        {
          id: 3,
          month: 'September',
          federal: this.sepfed,
          socialSecurity: this.sepss,
          medicare: this.sepmed,
          total: this.septax,
        },
      ];
    },
  },

  methods: {
    async getTaxYear() {
      this.isLoading = true;
      try {
        const response = await this.http1.http.get('/api/v2/pim/deductions');
        const {data} = response.data;
        this.thisYear = data.taxyear;
      } finally {
        this.isLoading = false;
      }
    },

    getStepClass(step) {
      const doneMap = {
        create: this.f941created,
        validate: this.f941validated,
        transmit: this.f941transmitted,
      };

      if (doneMap[step]) return 'is-done';
      if (this.efileRunning && this.efileStep === step) return 'is-active';
      return 'is-pending';
    },

    getStepSubtitle(step) {
      if (step === 'create') {
        if (this.f941created) return 'Form 941 created';
        if (this.efileRunning && this.efileStep === 'create') {
          return 'Creating now...';
        }
        return 'Waiting to create';
      }

      if (step === 'validate') {
        if (this.f941validated) return 'Form 941 validated';
        if (this.efileRunning && this.efileStep === 'validate') {
          return 'Validating now...';
        }
        return 'Waiting to validate';
      }

      if (step === 'transmit') {
        if (this.f941transmitted) return 'Form 941 transmitted';
        if (this.efileRunning && this.efileStep === 'transmit') {
          return 'Transmitting now...';
        }
        return 'Waiting to transmit';
      }

      return '';
    },

    onClickEdit($event) {
      const id = $event.empNumber ? $event.empNumber : $event.item?.empNumber;
      navigate('/pim/viewPersonalDetails/empNumber/{id}', {id});
    },

    async onClickeFile() {
      const confirmation = await this.$refs.confirmDialog.showDialog();

      if (confirmation !== 'ok') {
        return;
      }

      this.efileRunning = true;
      this.efileStep = '';
      this.efileMessage = 'Starting Form 941 e-file process...';

      try {
        const response = await this.http.http.get('/api/v2/pim/payreportsq3');
        const current = response.data.meta || response.data;

        if (current.created941 === 'No') {
          this.efileStep = 'create';
          this.efileMessage = 'Creating Form 941...';

          await this.http.http.get('/api/v2/pim/payreportsq3', {
            params: {
              action: 'create-941',
              taxYear: this.thisYear,
              quarter: 1,
            },
          });
        } else if (current.validated941 === 'No') {
          this.efileStep = 'validate';
          this.efileMessage = 'Validating Form 941...';

          await this.http.http.get('/api/v2/pim/payreportsq3', {
            params: {
              action: 'validate-941',
              taxYear: this.thisYear,
              quarter: 1,
            },
          });
        } else if (current.transmitted941 === 'No') {
          this.efileStep = 'transmit';
          this.efileMessage = 'Transmitting Form 941 to IRS...';

          await this.http.http.get('/api/v2/pim/payreportsq3', {
            params: {
              action: 'transmit-941',
              taxYear: this.thisYear,
              quarter: 1,
            },
          });
        } else {
          this.efileStep = 'transmit';
          this.efileMessage = 'Finalizing e-file submission...';

          await this.http.http.get('/api/v2/pim/payreportsq3', {
            params: {
              action: 'efile-941',
              taxYear: this.thisYear,
              quarter: 1,
            },
          });
        }

        await this.resetDataTable();

        if (this.items?.meta?.transmitted941 === 'Yes') {
          this.efileMessage = 'Form 941 successfully transmitted to IRS.';
        } else if (this.items?.meta?.validated941 === 'Yes') {
          this.efileMessage = 'Form 941 successfully validated.';
        } else if (this.items?.meta?.created941 === 'Yes') {
          this.efileMessage = 'Form 941 successfully created.';
        } else {
          this.efileMessage =
            this.items?.meta?.message || 'Form 941 step completed.';
        }
      } catch (error) {
        this.efileMessage =
          error?.response?.data?.message ||
          error?.message ||
          'An error occurred during Form 941 e-file. Please try again.';
      } finally {
        this.efileRunning = false;
        this.efileStep = '';
        await this.resetDataTable();
      }
    },

    onClickDownloadSuta() {
      const dash = '-q3';
      const pdf = '.csv';
      const year = this.thisYear;
      const fn = 'suta';
      const fl = fn.concat(year, dash, pdf);
      const emp = '1';
      const str1 = '/api/v2/pim/employees/';
      const str2 = '/screen/personal/attachments';
      const str = str1.concat(emp, str2);
      const sfiles = new APIService(window.appGlobal.baseUrl, str);

      sfiles.getAll().then((response) => {
        const dat = response.data;
        const fle = dat.data.find((f) => f.filename === fl);

        if (fle) {
          const id = fle.id;
          const downUrl = `${window.appGlobal.baseUrl}/pim/viewAttachment/empNumber/1/attachId/${id}`;
          window.open(downUrl, '_blank');
        }
      });
    },

    onClickDownload() {
      const dash = '-q3';
      const pdf = '.pdf';
      const year = this.thisYear;
      const fn = 'f941for';
      const fl = fn.concat(year, dash, pdf);
      const emp = '1';
      const str1 = '/api/v2/pim/employees/';
      const str2 = '/screen/personal/attachments';
      const str = str1.concat(emp, str2);
      const sfiles = new APIService(window.appGlobal.baseUrl, str);

      sfiles.getAll().then((response) => {
        const dat = response.data;
        const fle = dat.data.find((f) => f.filename === fl);

        if (fle) {
          const id = fle.id;
          const downUrl = `${window.appGlobal.baseUrl}/pim/viewAttachment/empNumber/1/attachId/${id}`;
          window.open(downUrl, '_blank');
        }
      });
    },

    async resetDataTable() {
      await this.execQuery();
    },
  },
};
</script>

<style scoped>
.taxbandits-stage-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.taxbandits-message-box {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.taxbandits-success-btn,
.taxbandits-success-btn:disabled {
  background-color: #34bc40 !important;
  border-color: #34bc40 !important;
  color: #ffffff !important;
  opacity: 1 !important;
}

.taxbandits-progress-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 1rem 0;
  flex-wrap: wrap;
}

.taxbandits-progress-step {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px;
}

.taxbandits-progress-dot {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  border: 2px solid #cfd3d7;
  background: #ffffff;
  color: #64707d;
}

.taxbandits-progress-text {
  display: flex;
  flex-direction: column;
}

.taxbandits-progress-title {
  font-weight: 700;
  font-size: 14px;
}

.taxbandits-progress-subtitle {
  font-size: 12px;
  color: #64707d;
}

.taxbandits-progress-line {
  width: 36px;
  height: 2px;
  background: #d9dee3;
}

.taxbandits-progress-step.is-done .taxbandits-progress-dot {
  background: #34bc40;
  border-color: #34bc40;
  color: #ffffff;
}

.taxbandits-progress-step.is-active .taxbandits-progress-dot {
  background: #ff920b;
  border-color: #ff920b;
  color: #ffffff;
}

.taxbandits-progress-step.is-done .taxbandits-progress-title {
  color: #34bc40;
}

.taxbandits-progress-step.is-active .taxbandits-progress-title {
  color: #ff920b;
}
</style>
